import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { TextInput, Button } from '@wot/components';
import workshopRegistrationsApi from '../../api/workshopRegistrations';
import { SPINNER } from '../../assets/animated';
import NoResults from '../../components/NoResults';
import Pagination from '../../components/Pagination';
import usePagainate from '../../hooks/usePaginate';
import TableContainer from './components/Table';

export default function Registrations() {
  const queryClient = useQueryClient();
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage, resetPage } = usePagainate();
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const filters: Record<string, string> = {};
  if (debouncedSearch) filters.search = debouncedSearch;

  const queryKey = ['workshop-registrations', currentPage, resultsPerPage, sortField, sortDir, debouncedSearch];

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey,
    queryFn: async () => await workshopRegistrationsApi.get(currentPage, resultsPerPage, filters, sortField, sortDir)
  });

  useEffect(() => {
    if (!isPreviousData && data && data.count > resultsPerPage * currentPage) {
      queryClient.prefetchQuery({
        queryKey: ['workshop-registrations', currentPage + 1, resultsPerPage, sortField, sortDir, debouncedSearch],
        queryFn: async () => await workshopRegistrationsApi.get(currentPage + 1, resultsPerPage, filters, sortField, sortDir)
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, isError, sortField, sortDir, debouncedSearch]);

  useEffect(() => {
    resetPage();
    setSelectedIds(new Set());
  }, [sortField, sortDir, debouncedSearch]);

  const handleSort = (key: string) => {
    if (sortField === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(key);
      setSortDir('asc');
    }
  };

  const handleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (!data) return;
    if (checked) {
      setSelectedIds(new Set(data.data.map((e) => e._id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} registration${selectedIds.size > 1 ? 's' : ''}?`)) return;
    try {
      await Promise.all(Array.from(selectedIds).map((id) => workshopRegistrationsApi.remove(id)));
      toast.success(`${selectedIds.size} registration${selectedIds.size > 1 ? 's' : ''} deleted`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['workshop-registrations'] });
    } catch {
      toast.error('Failed to delete registrations');
    }
  };

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow">
      <h1 className="text-xl font-semibold">Registrations</h1>

      <div className="flex items-center gap-4">
        <div className="w-72">
          <TextInput placeholder="Search by name, email..." value={search} onChange={setSearch} size="small" />
        </div>
        {selectedIds.size > 0 && (
          <Button
            type="primary"
            text={`Delete (${selectedIds.size})`}
            onClick={handleBulkDelete}
            className="!bg-red-600 !border-red-600 hover:!bg-red-700"
          />
        )}
      </div>

      {isLoading ? (
        <div className="w-full min-h-[256px] flex items-center justify-center">
          <img src={SPINNER} alt="loading" className="w-6 h-6" />
        </div>
      ) : data && data.data.length > 0 ? (
        <>
          <TableContainer
            handleSort={handleSort}
            data={data.data}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onSelectAll={handleSelectAll}
          />
          <Pagination
            count={data.count}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
          />
        </>
      ) : (
        <NoResults />
      )}
    </div>
  );
}
